import { useState } from 'react';
import { ExpansionCard, BodyShort, Heading } from '@navikt/ds-react';
import { translator } from 'translations';
import { AnalyticsEvents, logAnalyticsEvent } from 'utils/analytics';
import { usePageContentProps } from 'store/pageContext';
import { getDecoratorParams } from 'utils/decorator-utils';
import { innholdsTypeMap } from 'types/content-props/_content-common';
import { OfficeDetailsData } from 'types/content-props/office-details-props';
import { officeDetailsFormatAddress } from 'components/pages/office-page/officeDetails/utils';

import styles from './OfficeInformation.module.scss';

interface OfficeInformationProps {
    officeData: OfficeDetailsData;
    initialOpen?: boolean;
    hideLocation?: boolean;
    locationLabel?: string;
}

export const OfficeInformation = ({
    officeData,
    initialOpen = false,
    hideLocation,
    locationLabel,
}: OfficeInformationProps) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const contentProps = usePageContentProps();
    const { context } = getDecoratorParams(contentProps);
    const getOfficeTranslations = translator('office', contentProps.language);
    const title = getOfficeTranslations('officeInformation');
    const { postadresse, beliggenhet, organisasjonsnummer, enhetNr, faksnummer } = officeData;
    const visitingAddress = hideLocation ? '' : officeDetailsFormatAddress(beliggenhet, true);
    const postalAddress = officeDetailsFormatAddress(postadresse, true);
    const hasOfficeInformation =
        Boolean(visitingAddress) ||
        Boolean(postalAddress) ||
        Boolean(organisasjonsnummer) ||
        Boolean(enhetNr) ||
        Boolean(faksnummer);

    const toggleExpandCollapse = (isOpening: boolean, tittel: string) => {
        setIsOpen(isOpening);
        logAnalyticsEvent(
            isOpening ? AnalyticsEvents.ACCORDION_APNET : AnalyticsEvents.ACCORDION_LUKKET,
            {
                tittel,
                opprinnelse: 'kontorinformasjon',
                komponentId: 'OfficeInformation',
                målgruppe: context,
                innholdstype: innholdsTypeMap[contentProps.type],
            }
        );
    };

    if (!hasOfficeInformation) {
        return null;
    }

    return (
        <ExpansionCard
            size="small"
            aria-label={title}
            className={styles.officeInformation}
            onToggle={(isOpen) => toggleExpandCollapse(isOpen, title)}
            open={isOpen}
        >
            <ExpansionCard.Header className={styles.expansionCardHeader}>
                <ExpansionCard.Title as="h2" size="small">
                    {title}
                </ExpansionCard.Title>
            </ExpansionCard.Header>
            <ExpansionCard.Content>
                <div className={styles.expansionCardContent}>
                    {visitingAddress && (
                        <section>
                            <Heading level="3" size="small" spacing>
                                {locationLabel || getOfficeTranslations('location')}
                            </Heading>
                            {beliggenhet?.adresseTilleggsnavn && (
                                <BodyShort>{beliggenhet.adresseTilleggsnavn}</BodyShort>
                            )}
                            <BodyShort>{visitingAddress}</BodyShort>
                        </section>
                    )}
                    {postalAddress && (
                        <section>
                            <Heading level="3" size="small" spacing>
                                {getOfficeTranslations('postalAddress')}
                            </Heading>
                            <BodyShort>{postalAddress}</BodyShort>
                        </section>
                    )}
                    {organisasjonsnummer && (
                        <section>
                            <Heading level="3" size="small" spacing>
                                {getOfficeTranslations('orgNumber')}
                            </Heading>
                            <BodyShort>{organisasjonsnummer}</BodyShort>
                        </section>
                    )}
                    {enhetNr && (
                        <section>
                            <Heading level="3" size="small" spacing>
                                {getOfficeTranslations('officeNumber')}
                            </Heading>
                            <BodyShort>{enhetNr}</BodyShort>
                        </section>
                    )}
                    {faksnummer && (
                        <section>
                            <Heading level="3" size="small" spacing>
                                {getOfficeTranslations('faxNumber')}
                            </Heading>
                            <BodyShort>{faksnummer}</BodyShort>
                        </section>
                    )}
                </div>
            </ExpansionCard.Content>
        </ExpansionCard>
    );
};
